import { OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';

const ActivityTimeline = ({ activities, projectId }) => {
    
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMs = now - date;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        
        // If less than 1 minute
        if (diffInMinutes < 1) {
            return 'just now';
        }
        
        // If less than 60 minutes
        if (diffInMinutes < 60) {
            return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
        }
        
        // If less than 24 hours
        if (diffInHours < 24) {
            return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
        }
        
        // If less than 7 days
        if (diffInDays < 7) {
            return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
        }
        
        // Otherwise show formatted date
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActivityIcon = (type) => {
        const icons = {
            project: 'fa-folder-open',
            task: 'fa-check-circle',
            subtask: 'fa-list-ul',
            comment: 'fa-comment-dots',
            file: 'fa-paperclip',
            member: 'fa-user-friends',
            time: 'fa-clock',
            workflow: 'fa-sitemap',
            milestone: 'fa-flag-checkered',
            notification: 'fa-bell',
            financial: 'fa-dollar-sign',
            risk: 'fa-exclamation-triangle',
            approval: 'fa-check-square',
            dependency: 'fa-link',
            report: 'fa-chart-bar',
            tag: 'fa-tag',
            integration: 'fa-plug',
            security: 'fa-shield-alt',
            template: 'fa-clone',
            status: 'fa-sync-alt',
            assignment: 'fa-user-plus'
        };
        return icons[type] || 'fa-circle-notch';
    };

    const getActivityColor = (type) => {
        const colors = {
            project: '#ef4444',
            task: '#3b82f6',
            subtask: '#60a5fa',
            comment: '#10b981',
            file: '#06b6d4',
            member: '#8b5cf6',
            time: '#f59e0b',
            workflow: '#ec4899',
            milestone: '#14b8a6',
            notification: '#a855f7',
            financial: '#22c55e',
            risk: '#f97316',
            approval: '#16a34a',
            dependency: '#0ea5e9',
            report: '#6366f1',
            tag: '#84cc16',
            integration: '#06b6d4',
            security: '#dc2626',
            template: '#8b5cf6',
            status: '#f59e0b',
            assignment: '#a855f7'
        };
        return colors[type] || '#6b7280';
    };

    const renderMetadata = (activity) => {
        if (!activity.metadata) return null;

        if (activity.type === 'status') {
            return (
                <div className="activity-metadata">
                    <span className="metadata-badge badge-warning">{activity.metadata.from}</span>
                    <i className="fas fa-arrow-right mx-2"></i>
                    <span className="metadata-badge badge-success">{activity.metadata.to}</span>
                </div>
            );
        }

        if (activity.type === 'task' && activity.metadata.changes) {
            return (
                <div className="activity-metadata">
                    {activity.metadata.changes.map((change, index) => (
                        <div key={index} className="metadata-change">
                            <span className="change-field">{change.field}:</span>
                            <span className="change-from">{change.from}</span>
                            <i className="fas fa-arrow-right mx-1"></i>
                            <span className="change-to">{change.to}</span>
                        </div>
                    ))}
                </div>
            );
        }

        if (activity.type === 'file') {
            return (
                <div className="activity-metadata">
                    <Badge bg="secondary" className="me-2">
                        <i className="fas fa-file me-1"></i>
                        {activity.metadata.fileCount} files
                    </Badge>
                    <Badge bg="secondary">
                        <i className="fas fa-hdd me-1"></i>
                        {activity.metadata.totalSize}
                    </Badge>
                </div>
            );
        }

        if (activity.type === 'assignment' && activity.metadata.assignees) {
            return (
                <div className="activity-metadata">
                    <div className="assignees-list">
                        {activity.metadata.assignees.map((assignee, index) => (
                            <Badge key={index} bg="primary" className="me-1">
                                {assignee}
                            </Badge>
                        ))}
                    </div>
                </div>
            );
        }

        if (activity.type === 'member') {
            return (
                <div className="activity-metadata">
                    <Badge bg="info">
                        {activity.metadata.memberName} - {activity.metadata.role}
                    </Badge>
                </div>
            );
        }

        if (activity.type === 'project' && activity.metadata.changes) {
            return (
                <div className="activity-metadata">
                    {activity.metadata.changes.map((change, index) => (
                        <div key={index} className="metadata-change">
                            <span className="change-field">{change.field}:</span>
                            <span className="change-from">{change.from}</span>
                            <i className="fas fa-arrow-right mx-1"></i>
                            <span className="change-to">{change.to}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    };

    if (!activities || activities.length === 0) {
        return (
            <div className="activity-empty-state">
                <i className="fas fa-history"></i>
                <h4>No Activities Yet</h4>
                <p>Activities will appear here as team members work on this project.</p>
            </div>
        );
    }

    return (
        <div className="activity-timeline">
            {activities.map((activity, index) => (
                <div key={activity.id} className="activity-item">
                    <div className="activity-timeline-line">
                        {index !== activities.length - 1 && <div className="timeline-connector"></div>}
                    </div>
                    <div 
                        className="activity-icon" 
                        style={{ backgroundColor: getActivityColor(activity.type) }}
                    >
                        <i className={`fas ${getActivityIcon(activity.type)}`}></i>
                    </div>
                    <div className="activity-content">
                        <div className="activity-header">
                            <div className="activity-user-info">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>{activity.user.name}</Tooltip>}
                                >
                                    <div className="activity-avatar">
                                        {activity.user.avatar ? (
                                            <img src={activity.user.avatar} alt={activity.user.name} />
                                        ) : (
                                            <div className="avatar-initials">{activity.user.initials}</div>
                                        )}
                                    </div>
                                </OverlayTrigger>
                                <div className="activity-details">
                                    <div className="activity-user-name">{activity.user.name}</div>
                                    <div className="activity-action-title">
                                        <span className="action-text">{activity.action}</span>
                                        {activity.title && (
                                            <>
                                                <span className="action-separator">·</span>
                                                <span className="activity-title">{activity.title}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="activity-timestamp">
                                {formatTimestamp(activity.timestamp)}
                            </div>
                        </div>
                        {activity.description && (
                            <div className="activity-description">
                                {activity.description}
                            </div>
                        )}
                        {renderMetadata(activity)}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ActivityTimeline;
